/**
 * Integrated Dashboard - Phase 4 Integration
 * Combines Team A dashboard components with Team B interactive features
 * Full integration of Magic UI, 3D visualization, and real-time systems
 */

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  TrendingUp,
  Users,
  Leaf,
  Settings,
  Maximize2,
  Minimize2,
  RefreshCw,
  Activity,
  Globe,
  Zap,
} from 'lucide-react';

// Team A Components
import { BentoGrid, BentoGridItem } from '../common/BentoGrid';
import { MagicCard } from '../ui/magic/MagicCard';
import { NumberTicker } from '../ui/magic/NumberTicker';
import { PerformanceMetrics } from '../dashboard/PerformanceMetrics';
import { SustainabilityDashboard } from '../sustainability/SustainabilityDashboard';
import { StakeholderInterface } from '../collaboration/StakeholderInterface';

// Team B Components (will import once we have access to them)
// import { RouteVisualization } from '../visualization/RouteVisualization';
// import { RealTimeStatus } from '../status/RealTimeStatus';
// import { AdvancedVehicleTracking } from '../tracking/AdvancedVehicleTracking';
// import { InteractiveOptimization } from '../optimization/InteractiveOptimization';

import { cn } from '../../lib/utils';
import { useProject } from '../../contexts/ProjectContext';

// For now, we'll create placeholder components for Team B features
const RouteVisualizationPlaceholder: React.FC<{ className?: string }> = ({ className }) => (
  <MagicCard className={cn('p-6 h-96', className)}>
    <div className="flex items-center space-x-2 mb-4">
      <Globe className="w-5 h-5 text-blue-600" />
      <h3 className="text-lg font-semibold text-gray-900">3D Route Visualization</h3>
    </div>
    <div className="flex items-center justify-center h-full bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg">
      <div className="text-center">
        <Globe className="w-16 h-16 text-blue-400 mx-auto mb-4" />
        <p className="text-gray-600">Team B 3D Visualization Component</p>
        <p className="text-sm text-gray-500 mt-1">Globe + AnimatedBeam + OrbitingCircles</p>
      </div>
    </div>
  </MagicCard>
);

const RealTimeStatusPlaceholder: React.FC<{ className?: string }> = ({ className }) => (
  <MagicCard className={cn('p-4', className)}>
    <div className="flex items-center space-x-2 mb-3">
      <Activity className="w-4 h-4 text-green-600" />
      <h4 className="font-medium text-gray-900">Live Status Updates</h4>
    </div>
    <div className="space-y-2">
      <div className="bg-green-50 p-2 rounded text-sm text-green-700">
        📡 Team B MarqueeDemo + AnimatedList + Ripple
      </div>
      <div className="bg-blue-50 p-2 rounded text-sm text-blue-700">
        🚛 Vehicle tracking active
      </div>
    </div>
  </MagicCard>
);

const VehicleTrackingPlaceholder: React.FC<{ className?: string }> = ({ className }) => (
  <MagicCard className={cn('p-4', className)}>
    <div className="flex items-center space-x-2 mb-3">
      <Zap className="w-4 h-4 text-purple-600" />
      <h4 className="font-medium text-gray-900">Advanced Vehicle Tracking</h4>
    </div>
    <div className="bg-purple-50 p-3 rounded">
      <p className="text-sm text-purple-700">Team B 3D tracking system</p>
      <p className="text-xs text-purple-600 mt-1">Real-time position updates + traffic indicators</p>
    </div>
  </MagicCard>
);

interface IntegratedDashboardProps {
  className?: string;
  layout?: 'standard' | 'compact' | 'detailed';
  enableRealTime?: boolean;
  showTeamBComponents?: boolean;
}

interface DashboardSection {
  id: string;
  title: string;
  component: React.ComponentType<any>;
  teamOrigin: 'A' | 'B' | 'Integrated';
  priority: 'high' | 'medium' | 'low';
  size: 'small' | 'medium' | 'large' | 'xlarge';
  dependencies?: string[];
}

export const IntegratedDashboard: React.FC<IntegratedDashboardProps> = ({
  className,
  layout = 'standard',
  enableRealTime = true,
  showTeamBComponents = true,
}) => {
  const { currentProject } = useProject();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Integration state management
  const [integrationMetrics, setIntegrationMetrics] = useState({
    teamAComponentsLoaded: 0,
    teamBComponentsLoaded: 0,
    crossComponentSync: true,
    performanceScore: 95,
    lastSyncTime: new Date(),
  });

  // Define dashboard sections with Team A/B integration
  const dashboardSections: DashboardSection[] = useMemo(() => [
    {
      id: 'performance-metrics',
      title: 'Performance Analytics',
      component: PerformanceMetrics,
      teamOrigin: 'A',
      priority: 'high',
      size: 'large',
    },
    {
      id: 'route-visualization',
      title: '3D Route Visualization',
      component: RouteVisualizationPlaceholder,
      teamOrigin: 'B',
      priority: 'high',
      size: 'xlarge',
      dependencies: ['performance-metrics'],
    },
    {
      id: 'sustainability-analytics',
      title: 'Sustainability Analytics',
      component: SustainabilityDashboard,
      teamOrigin: 'A',
      priority: 'high',
      size: 'large',
    },
    {
      id: 'real-time-status',
      title: 'Live Status Updates',
      component: RealTimeStatusPlaceholder,
      teamOrigin: 'B',
      priority: 'medium',
      size: 'medium',
      dependencies: ['performance-metrics'],
    },
    {
      id: 'stakeholder-collaboration',
      title: 'Stakeholder Collaboration',
      component: StakeholderInterface,
      teamOrigin: 'A',
      priority: 'medium',
      size: 'large',
    },
    {
      id: 'vehicle-tracking',
      title: 'Advanced Vehicle Tracking',
      component: VehicleTrackingPlaceholder,
      teamOrigin: 'B',
      priority: 'medium',
      size: 'medium',
      dependencies: ['sustainability-analytics'],
    },
  ], []);

  // Filter sections based on layout and team component availability
  const activeSections = useMemo(() => {
    let sections = dashboardSections;
    
    if (!showTeamBComponents) {
      sections = sections.filter(s => s.teamOrigin !== 'B');
    }
    
    if (layout === 'compact') {
      sections = sections.filter(s => s.priority === 'high');
    }
    
    return sections;
  }, [dashboardSections, layout, showTeamBComponents]);

  // Calculate grid layout based on section sizes
  const getGridLayout = () => {
    switch (layout) {
      case 'compact':
        return { sm: 1, md: 2, lg: 2 };
      case 'detailed':
        return { sm: 1, md: 2, lg: 3 };
      default:
        return { sm: 1, md: 2, lg: 3 };
    }
  };

  const getBentoSize = (size: DashboardSection['size']) => {
    switch (size) {
      case 'small': return 'col-span-1 row-span-1';
      case 'medium': return 'col-span-1 md:col-span-1 row-span-1';
      case 'large': return 'col-span-1 md:col-span-2 row-span-1';
      case 'xlarge': return 'col-span-1 md:col-span-2 lg:col-span-3 row-span-2';
      default: return 'col-span-1 row-span-1';
    }
  };

  // Handle real-time updates and cross-component synchronization
  useEffect(() => {
    if (!enableRealTime) return;

    const syncInterval = setInterval(() => {
      setIntegrationMetrics(prev => ({
        ...prev,
        teamAComponentsLoaded: activeSections.filter(s => s.teamOrigin === 'A').length,
        teamBComponentsLoaded: showTeamBComponents ? activeSections.filter(s => s.teamOrigin === 'B').length : 0,
        lastSyncTime: new Date(),
        performanceScore: Math.min(95 + Math.random() * 5, 100),
      }));
    }, 5000);

    return () => clearInterval(syncInterval);
  }, [enableRealTime, activeSections, showTeamBComponents]);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    setIntegrationMetrics(prev => ({
      ...prev,
      lastSyncTime: new Date(),
    }));
  };

  const handleSectionToggle = (sectionId: string) => {
    setActiveSection(prev => prev === sectionId ? null : sectionId);
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Integrated Dashboard Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100">
            <LayoutDashboard className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Integrated VRP Dashboard</h1>
            <p className="text-gray-600">
              Combined Team A + Team B Features • {currentProject?.name || 'All Projects'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Integration Status */}
          {enableRealTime && (
            <motion.div
              className="flex items-center space-x-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span>Integrated System Live</span>
            </motion.div>
          )}
          
          {/* Controls */}
          <button
            onClick={handleRefresh}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
          
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Integration Metrics */}
      <BentoGrid columns={{ sm: 2, md: 4, lg: 4 }} gap={4}>
        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <TrendingUp className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <NumberTicker
              value={integrationMetrics.teamAComponentsLoaded}
              className="text-xl font-bold text-blue-600"
              duration={1.5}
            />
            <p className="text-xs text-gray-600">Team A Components</p>
          </MagicCard>
        </BentoGridItem>
        
        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Globe className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <NumberTicker
              value={integrationMetrics.teamBComponentsLoaded}
              className="text-xl font-bold text-purple-600"
              duration={1.7}
            />
            <p className="text-xs text-gray-600">Team B Components</p>
          </MagicCard>
        </BentoGridItem>
        
        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Activity className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <NumberTicker
              value={integrationMetrics.performanceScore}
              suffix="%"
              className="text-xl font-bold text-green-600"
              duration={1.9}
            />
            <p className="text-xs text-gray-600">Performance Score</p>
          </MagicCard>
        </BentoGridItem>
        
        <BentoGridItem>
          <MagicCard className="p-4 text-center">
            <Zap className="w-6 h-6 text-orange-600 mx-auto mb-2" />
            <div className="text-lg font-bold text-orange-600">
              {integrationMetrics.crossComponentSync ? 'SYNC' : 'ASYNC'}
            </div>
            <p className="text-xs text-gray-600">Component Sync</p>
          </MagicCard>
        </BentoGridItem>
      </BentoGrid>

      {/* Main Dashboard Grid */}
      <div className={cn(
        'grid gap-6',
        isFullscreen && 'fixed inset-0 z-50 bg-white p-6 overflow-auto'
      )}>
        <BentoGrid columns={getGridLayout()} gap={6}>
          {activeSections.map((section, index) => {
            const Component = section.component;
            const isActive = activeSection === section.id;
            
            return (
              <BentoGridItem 
                key={`${section.id}-${refreshTrigger}`}
                priority={section.priority}
                className={getBentoSize(section.size)}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className={cn(
                    'h-full',
                    isActive && 'ring-2 ring-blue-500'
                  )}
                >
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <div className={cn(
                        'w-3 h-3 rounded-full',
                        section.teamOrigin === 'A' ? 'bg-blue-500' : 
                        section.teamOrigin === 'B' ? 'bg-purple-500' : 'bg-green-500'
                      )} />
                      <span className="text-sm font-medium text-gray-700">
                        {section.title} • Team {section.teamOrigin}
                      </span>
                    </div>
                    
                    <button
                      onClick={() => handleSectionToggle(section.id)}
                      className="p-1 hover:bg-gray-100 rounded text-gray-500"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Component */}
                  <Component
                    key={`component-${refreshTrigger}`}
                    className="h-full"
                    compactMode={layout === 'compact'}
                    enableRealTimeUpdates={enableRealTime}
                    showTeamBComponents={showTeamBComponents}
                  />
                </motion.div>
              </BentoGridItem>
            );
          })}
        </BentoGrid>
      </div>

      {/* Integration Status Footer */}
      <MagicCard className="p-4 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              <span>Team A: Dashboard + Analytics + Collaboration</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-purple-500 rounded-full" />
              <span>Team B: 3D Visualization + Real-time + Tracking</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Activity className="w-4 h-4" />
            <span>Last sync: {integrationMetrics.lastSyncTime.toLocaleTimeString()}</span>
          </div>
        </div>
      </MagicCard>
    </div>
  );
};